import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { displayValue } from '../../lib/format-value';
import { optionSets } from '../../lib/option-sets';
import { getEntityConfig, type FormFieldConfig } from '../../lib/entity-config';
import { useEntities } from '../../hooks/useEntities';

// ---------------------------------------------------------------------------
// FkSelect — separate component so useEntities hook is called at component
// top-level (not inside the fields.map() loop, which would violate Rules of Hooks)
// ---------------------------------------------------------------------------
interface FkSelectProps {
  field: FormFieldConfig;
  value: any;
  onChange: (value: any) => void;
}

function FkSelect({ field, value, onChange }: FkSelectProps) {
  const { records, isLoading } = useEntities(field.targetEntity!);

  // Resolve nameField from target entity config, fall back to common names
  const targetConfig = getEntityConfig(field.targetEntity!);
  const nameField = targetConfig?.nameField;

  function getRecordName(record: { Id: string; Attributes: { Key: string; Value: any }[] }): string {
    // Try the configured nameField first
    if (nameField) {
      const attr = record.Attributes.find(a => a.Key === nameField);
      if (attr) return displayValue(attr.Value);
    }
    // Fallback: try common name fields
    for (const fallback of ['fullname', 't4a_name', 'name']) {
      const attr = record.Attributes.find(a => a.Key === fallback);
      if (attr && attr.Value) return displayValue(attr.Value);
    }
    return record.Id;
  }

  // Extract the currently selected ID from either {Id, Name, LogicalName} or a bare string
  const selectedId =
    typeof value === 'object' && value !== null && 'Id' in value
      ? value.Id
      : typeof value === 'string'
        ? value
        : '';

  function handleSelect(e: React.ChangeEvent<HTMLSelectElement>) {
    const selectedRecordId = e.target.value;
    if (!selectedRecordId) {
      onChange(null);
      return;
    }
    const record = records.find(r => r.Id === selectedRecordId);
    if (record) {
      onChange({
        Id: record.Id,
        Name: getRecordName(record),
        LogicalName: field.targetEntity!,
      });
    }
  }

  const selectClass =
    'w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white';

  if (isLoading) {
    return (
      <select disabled className={selectClass}>
        <option>Loading...</option>
      </select>
    );
  }

  return (
    <select value={selectedId} onChange={handleSelect} className={selectClass}>
      <option value="">-- Select --</option>
      {records.map(record => (
        <option key={record.Id} value={record.Id}>
          {getRecordName(record)}
        </option>
      ))}
    </select>
  );
}

interface EntityFormProps {
  entityName: string;
  displayName: string;
  fields: FormFieldConfig[];
  initialValues?: Record<string, any>;
  onSubmit: (values: Record<string, any>) => void;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

export function EntityForm({
  entityName,
  displayName,
  fields,
  initialValues = {},
  onSubmit,
  isEdit = false,
  isSubmitting = false,
}: EntityFormProps) {
  const [values, setValues] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    for (const field of fields) {
      initial[field.key] = initialValues[field.key] ?? '';
    }
    return initial;
  });

  function handleChange(key: string, value: any) {
    setValues(prev => ({ ...prev, [key]: value }));
  }

  // -------------------------------------------------------------------------
  // Field input rendering — extracted so it can be reused in both the regular
  // grid and inside collapsible sections without duplicating the ternary chain
  // -------------------------------------------------------------------------
  function renderFieldInput(field: FormFieldConfig) {
    if (field.type === 'textarea') {
      return (
        <textarea
          value={values[field.key] ?? ''}
          onChange={e => handleChange(field.key, e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }
    if (field.type === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={!!values[field.key]}
          onChange={e => handleChange(field.key, e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-blue-600"
        />
      );
    }
    if (field.type === 'number') {
      return (
        <input
          type="number"
          step="any"
          value={values[field.key] ?? ''}
          onChange={e => handleChange(field.key, e.target.value ? Number(e.target.value) : '')}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }
    if (field.type === 'date') {
      return (
        <input
          type="date"
          value={values[field.key] ? String(values[field.key]).split('T')[0] : ''}
          onChange={e => handleChange(field.key, e.target.value || '')}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }
    if (field.type === 'optionSet' && field.optionSetKey && optionSets[field.optionSetKey]) {
      return (
        <select
          value={
            (() => {
              const v = values[field.key];
              if (typeof v === 'object' && v !== null && 'Value' in v) return String(v.Value);
              if (v !== '' && v !== null && v !== undefined) return String(v);
              return '';
            })()
          }
          onChange={e => {
            const selected = e.target.value;
            handleChange(field.key, selected === '' ? null : { Value: Number(selected) });
          }}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="">-- Select --</option>
          {Object.entries(optionSets[field.optionSetKey]).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
      );
    }
    if (field.type === 'fk' && field.targetEntity) {
      return (
        <FkSelect
          field={field}
          value={values[field.key]}
          onChange={val => handleChange(field.key, val)}
        />
      );
    }
    return (
      <input
        type="text"
        value={typeof values[field.key] === 'object' ? displayValue(values[field.key]) : (values[field.key] ?? '')}
        onChange={e => handleChange(field.key, e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    );
  }

  // -------------------------------------------------------------------------
  // Renders all fields — regular ones in a grid, sectioned ones in collapsible
  // <details> blocks with sub-headings per sectionLabel
  // -------------------------------------------------------------------------
  function renderFields() {
    const regularFields = fields.filter(f => !f.section);
    const sectionedFields = fields.filter(f => f.section);

    // Group sectioned fields by section name (preserving order)
    const sectionMap = new Map<string, FormFieldConfig[]>();
    for (const field of sectionedFields) {
      const existing = sectionMap.get(field.section!) ?? [];
      existing.push(field);
      sectionMap.set(field.section!, existing);
    }

    return (
      <>
        {/* Regular fields in the existing 2-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {regularFields.map(field => (
            <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : undefined}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderFieldInput(field)}
            </div>
          ))}
        </div>

        {/* Collapsible sections */}
        {[...sectionMap.entries()].map(([sectionName, sectionFields]) => {
          // Group by sectionLabel within the section (preserving order)
          const subGroups: { label: string; fields: FormFieldConfig[] }[] = [];
          for (const field of sectionFields) {
            const label = field.sectionLabel ?? '';
            const last = subGroups[subGroups.length - 1];
            if (last && last.label === label) {
              last.fields.push(field);
            } else {
              subGroups.push({ label, fields: [field] });
            }
          }

          return (
            <details key={sectionName} className="mt-6 border border-slate-200 rounded-lg">
              <summary className="px-4 py-3 cursor-pointer text-base font-semibold text-slate-800 bg-slate-50 rounded-lg select-none hover:bg-slate-100">
                {sectionName}
              </summary>
              <div className="p-4 space-y-6">
                {subGroups.map(group => (
                  <div key={group.label} className="border-b border-slate-100 pb-4 last:border-b-0 last:pb-0">
                    {group.label && (
                      <h4 className="text-sm font-semibold text-slate-600 mb-3">{group.label}</h4>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                      {group.fields.map(field => (
                        <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : undefined}>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {renderFieldInput(field)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          );
        })}
      </>
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Build a set of date field keys for formatting
    const dateKeys = new Set(fields.filter(f => f.type === 'date').map(f => f.key));

    // Filter out empty values and format dates as YYYY-MM-DDT00:00:00
    const filtered: Record<string, any> = {};
    for (const [key, val] of Object.entries(values)) {
      if (val !== '' && val !== null && val !== undefined) {
        if (dateKeys.has(key) && typeof val === 'string' && val) {
          // Append T00:00:00 only if the value doesn't already contain 'T'
          // (prevents double-suffix on values loaded from existing records)
          filtered[key] = val.includes('T') ? val : `${val}T00:00:00`;
        } else {
          filtered[key] = val;
        }
      }
    }
    onSubmit(filtered);
  }

  return (
    <div>
      <div className="mb-6">
        <Link to={`/${entityName}`} className="text-sm text-blue-600 hover:text-blue-800 mb-1 inline-block">
          Back to {displayName} list
        </Link>
        <h2 className="text-xl font-semibold text-slate-900">
          {isEdit ? 'Edit' : 'Create'} {displayName}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-slate-200 p-6">
        {renderFields()}

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
          <Link
            to={`/${entityName}`}
            className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
